import { useState, useMemo } from 'react'
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
} from '@fluentui/react'

interface DataTableProps<T> {
  items: T[]
  columns: IColumn[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  onItemClick?: (item: T) => void
  selectionMode?: SelectionMode
}

export function DataTable<T extends object>({
  items,
  columns: initialColumns,
  loading = false,
  error = null,
  emptyMessage = 'No items found',
  onItemClick,
  selectionMode = SelectionMode.none,
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | undefined>()
  const [sortDescending, setSortDescending] = useState(false)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  // Handle column resize - stores the new width when user drags column border
  const handleColumnResize = (column?: IColumn, newWidth?: number) => {
    if (column && newWidth) {
      setColumnWidths(prev => ({
        ...prev,
        [column.key]: newWidth,
      }))
    }
  }

  // Add sorting and resizing capability to columns
  const columns = useMemo(() => {
    return initialColumns.map(col => {
      const resizedWidth = columnWidths[col.key]
      return {
        ...col,
        // Enable resizing on all columns (can be overridden by setting isResizable: false)
        isResizable: col.isResizable !== false,
        // Apply user-resized width - only set minWidth, remove maxWidth to allow free resizing
        minWidth: resizedWidth || col.minWidth || 50,
        maxWidth: col.isResizable === false ? col.maxWidth : undefined,
        currentWidth: resizedWidth,
        isSorted: col.key === sortColumn,
        isSortedDescending: col.key === sortColumn && sortDescending,
        onColumnClick: col.fieldName ? () => {
          if (sortColumn === col.key) {
            setSortDescending(!sortDescending)
          } else {
            setSortColumn(col.key)
            setSortDescending(false)
          }
        } : undefined,
      }
    })
  }, [initialColumns, sortColumn, sortDescending, columnWidths])

  // Sort items locally
  const sortedItems = useMemo(() => {
    if (!sortColumn) return items

    const col = columns.find(c => c.key === sortColumn)
    if (!col?.fieldName) return items

    const fieldName = col.fieldName as keyof T

    return [...items].sort((a, b) => {
      const aVal = a[fieldName]
      const bVal = b[fieldName]

      // Handle nulls
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDescending ? -1 : 1
      if (bVal == null) return sortDescending ? 1 : -1

      // Compare values
      let result = 0
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        result = aVal.localeCompare(bVal)
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        result = aVal - bVal
      } else {
        result = String(aVal).localeCompare(String(bVal))
      }

      return sortDescending ? -result : result
    })
  }, [items, sortColumn, sortDescending, columns])
  if (loading) {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        styles={{ root: { minHeight: 200 } }}
      >
        <Spinner size={SpinnerSize.large} label="Loading..." />
      </Stack>
    )
  }

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
    )
  }

  if (items.length === 0) {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        styles={{
          root: {
            minHeight: 200,
            backgroundColor: '#faf9f8',
            borderRadius: 4,
          },
        }}
      >
        <Text variant="large" styles={{ root: { color: '#605e5c' } }}>
          {emptyMessage}
        </Text>
      </Stack>
    )
  }

  return (
    <div className="table-container" style={{ overflowX: 'auto' }}>
      <DetailsList
        items={sortedItems}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selectionMode={selectionMode}
        onItemInvoked={onItemClick}
        onColumnResize={handleColumnResize}
        isHeaderVisible={true}
        styles={{
          root: {
            backgroundColor: '#ffffff',
            borderRadius: 4,
            overflow: 'hidden',
          },
          headerWrapper: {
            '& .ms-DetailsHeader': {
              backgroundColor: '#faf9f8',
              borderBottom: '1px solid #edebe9',
            },
            '& .ms-DetailsHeader-cell': {
              cursor: 'pointer',
            },
            '& .ms-DetailsHeader-cell:hover': {
              backgroundColor: '#f3f2f1',
            },
          },
          contentWrapper: {
            // Text truncation styles - content will show ellipsis when column is too narrow
            '& .ms-DetailsRow-cell': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            // Allow text to wrap and show full content on hover via title attribute
            '& .ms-DetailsRow-cell > *': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
          },
        }}
      />
    </div>
  )
}
